import React from 'react';
import ReactDOM from 'react-dom';
import { Trans, useTranslation } from 'react-i18next';
import { SellListing } from './useTipBuyer';
import { CloseIcon } from '../CloseIcon';
import { formatCardPrice } from '../../ListingCard/cardUtils';
import LoadingLabel from '../LoadingLabel';

export interface TipPreviewModalProps {
  isOpen: boolean;
  selectedListing: SellListing | null;
  isSendingTip: boolean;
  tipSentSuccess: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export const TipPreviewModal: React.FC<TipPreviewModalProps> = ({
  isOpen,
  selectedListing,
  isSendingTip,
  tipSentSuccess,
  onConfirm,
  onClose,
}) => {
  const { t } = useTranslation();
  if (!isOpen || !selectedListing) return null;

  const modalContent = (
    <div className="tip-preview-modal-overlay" onClick={onClose}>
          <div className={`end-chat-confirmation tip-preview-modal${tipSentSuccess ? ' tip-preview-modal--success' : ''}`} onClick={(e) => e.stopPropagation()}>
            {tipSentSuccess ? (
              /* Success Celebration State */
              <div className="tip-preview-modal__success">
                <div className="tip-preview-modal__success__icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path 
                      d="M20 6L9 17L4 12" 
                      stroke="currentColor" 
                      strokeWidth="2.5" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                      className="tip-preview-modal__success__checkmark"
                    />
                  </svg>
                </div>
                <h3 className="tip-preview-modal__success__title">
                  <Trans i18nKey="tipBuyer.successTitle">Tip sent!</Trans>
                </h3>
                <p className="tip-preview-modal__success__subtitle">
                  <Trans i18nKey="tipBuyer.successSubtitle">The buyer will be notified about your property</Trans>
                </p>
              </div>
            ) : (
              /* Normal Preview State */
              <>
                <div className="end-chat-confirmation__header tip-preview-modal__header">
                  <h3>
                    <Trans i18nKey="tipBuyer.confirmTitle">Tip this buyer about your property?</Trans>
                  </h3>
                  <button
                    className="end-chat-confirmation__close"
                    onClick={onClose}
                    disabled={isSendingTip}
                  >
                    <CloseIcon color="#202125" size={16} />
                  </button>
                </div>
                <div className="end-chat-confirmation__content tip-preview-modal__content">
                  <p className="tip-preview-modal__description">
                    <Trans i18nKey="tipBuyer.confirmDescription">
                      The buyer will receive a message from Jaakko about your property.
                    </Trans>
                  </p>

                  {/* Preview of the listing */}
                  <div className="tip-preview-modal__listing-preview">
                    <div className="tip-preview-modal__listing-preview__header">
                      <img
                        src="/assets/jaakko_head.png"
                        alt="Jaakko"
                        className="tip-preview-modal__listing-preview__avatar"
                      />
                      <span className="tip-preview-modal__listing-preview__sender">Jaakko</span>
                    </div>
                    <div className="tip-preview-modal__listing-preview__message">
                      <Trans i18nKey="tipBuyer.messagePreview">A seller tipped you about this property:</Trans>
                    </div>
                    <div className="tip-preview-modal__listing-preview__card">
                      {selectedListing.images && selectedListing.images.length > 0 && (
                        <img
                          src={selectedListing.images[0].thumbnailUrl || selectedListing.images[0].url}
                          alt=""
                          className="tip-preview-modal__listing-preview__card__image"
                        />
                      )}
                      <div className="tip-preview-modal__listing-preview__card__info">
                        <div className="tip-preview-modal__listing-preview__card__address">
                          {selectedListing.address_string}
                        </div>
                        <div className="tip-preview-modal__listing-preview__card__details">
                          {selectedListing.property_rooms && (
                            <span>{selectedListing.property_rooms} {t('building.offMarket.modal.rooms', { count: Number(selectedListing.property_rooms) || 0 })}</span>
                          )}
                          {selectedListing.property_size && (
                            <span>{selectedListing.property_size} m²</span>
                          )}
                          {selectedListing.property_asking_price && (
                            <span>{formatCardPrice(selectedListing.property_asking_price)}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="tip-preview-modal__actions">
                  <button
                    className="tip-preview-modal__cancel"
                    onClick={onClose}
                    disabled={isSendingTip}
                  >
                    <Trans i18nKey="building.offMarket.modal.cancel">Cancel</Trans>
                  </button>
                  <button
                    className="tip-preview-modal__confirm"
                    onClick={onConfirm}
                    disabled={isSendingTip}
                  >
                    <LoadingLabel loading={isSendingTip} loadingContent={t('building.offMarket.modal.loading', 'Loading...')}>
                      <Trans i18nKey="tipBuyer.sendTip">Send tip</Trans>
                    </LoadingLabel>
                  </button>
                </div>
              </>
            )}
          </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};
